import React, { useContext, useEffect } from 'react'
import { t } from '../../i18n/i18n'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { useForm } from 'react-hook-form'
import { ModalActions } from '../common/modal/Modal'
import { Button } from '@fluentui/react-components'
import { addProcessOwner, deleteProcessOwner, isSystemAdminOrCreator, updateProcessOwner } from '../../helpers/settingsHelper'
import { Process, ProcessOwnerDto, UpsertProcessOwnerDto, Workplace } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { ControlledMultiSelectDropdown } from '../common/inputs/ControlledDropdownMulti'
import { ProcessOwnerPicker } from './ProcessOwnerPicker'
import { DraggableModal } from '../common/modal/DraggableModal'
import { MoreOptionsMenuButton } from '../common/other/MoreOptionsMenuButton'
import { isSystemAdmin } from '../../helpers/permissionHelper'
import { Form } from '../common/form/Form'

type ModalUpsertProcessOwnerProps = {
   processOwner: ProcessOwnerDto | null
   open: boolean
   setOpen: React.Dispatch<React.SetStateAction<boolean>>
   allWorkplaces: Workplace[]
   allProcesses: Process[]
   setAllProcessOwners: React.Dispatch<React.SetStateAction<ProcessOwnerDto[]>>
   allProcessOwners: ProcessOwnerDto[]
   // processOwnersInUseMap?: Record<string, boolean>
}
export const ModalUpsertProcessOwner = ({
   processOwner,
   open,
   setOpen,
   allWorkplaces,
   allProcesses,
   setAllProcessOwners,
   allProcessOwners = [],
}: // processOwnersInUseMap,
ModalUpsertProcessOwnerProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount, organisation } = dataContext.state
   const processOwnerForm = useForm<UpsertProcessOwnerDto>({
      disabled: processOwner ? processOwner.id && !isSystemAdminOrCreator(processOwner, userAccount) : false,
   })
   const {
      control,
      reset,
      handleSubmit,
      formState: { disabled, isDirty, isSubmitting },
      setError,
   } = processOwnerForm
   const close = () => {
      setOpen(false)
      reset()
   }

   const occupiedProcesses = allProcessOwners
      .filter((ap) => ap.id !== processOwner?.id) // Exclude the one we are currently editing
      .flatMap((ap) => ap.processes)

   const isWorkplaceDisabled = organisation.workplaceLimit === 0
   const defaultWorkplace = allWorkplaces.find((wp) => wp.isDefault)

   useEffect(() => {
      if (processOwner) {
         reset(processOwner)
      } else {
         reset({
            userAccount: null,
            ownerFreeText: null,
            processes: [],
            workplaces: defaultWorkplace ? [defaultWorkplace] : [],
         })
      }
   }, [reset, processOwner, open, isWorkplaceDisabled, defaultWorkplace, allWorkplaces])

   const onSubmit = async (data: UpsertProcessOwnerDto) => {
      if (!data.userAccount && !data.ownerFreeText) {
         setError('userAccount', { message: t('RequiredField'), type: 'required' })

         // Force default workplace
         data.workplaces = [defaultWorkplace]
      } else {
         if (processOwner?.id) {
            await updateProcessOwner(setAllProcessOwners, dataContext, data, processOwner.id)
         } else {
            await addProcessOwner(setAllProcessOwners, dataContext, data)
         }
         close()
      }
   }

   const deleteOptions = () => {
      // const processOwnerInUse = processOwnersInUseMap[processOwner.id] ?? false
      const userHasPermission = isSystemAdmin(userAccount)

      // const deleteDisabled = !userHasPermission || processOwnerInUse
      const deleteDisabled = !userHasPermission
      // const deleteTooltipMessage = deleteDisabled
      //    ? !userHasPermission
      //       ? t('OnlySystemAdministratorCanDeleteProcessOwners')
      //       : t('TheProcessOwnerIsInUseAndCannotBeDeleted')
      //    : ''
      const deleteTooltipMessage = deleteDisabled ? t('OnlySystemAdministratorCanDeleteProcessOwners') : ''

      return (
         <MoreOptionsMenuButton
            item={processOwner}
            deleteFunction={deleteProcessOwner}
            setState={setAllProcessOwners}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={t('DeleteItem', { Item: t('ProcessOwner').toLowerCase() })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('TheProcessOwner').toLowerCase() })}
            closeOnDelete={close}
         />
      )
   }

   return (
      <DraggableModal
         title={processOwner ? t('EditItem', { Item: t('ProcessOwner') }) : t('NewProcessOwner')}
         open={open}
         onClose={close}
         secondaryButton={processOwner ? deleteOptions : null}
      >
         <Form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
               <GridItem size="12/12">
                  <ProcessOwnerPicker processOwnerForm={processOwnerForm} disabled={disabled} required />
               </GridItem>

               {/* Process dropdown */}
               <GridItem size="12/12">
                  <ControlledMultiSelectDropdown<Process>
                     name="processes"
                     control={control}
                     label={t('Processes')}
                     occupiedOptions={occupiedProcesses}
                     options={allProcesses}
                     getId={(p) => p.id}
                     getLabel={(p) => p.text ?? ''}
                     positioning={{ position: 'after', align: 'start', coverTarget: true }}
                     required
                  />
               </GridItem>

               {/* Hidden for now */}
               {/* Workplace dropdown */}
               {/* <GridItem size="12/12">
                  <ControlledMultiSelectDropdown<Workplace>
                     name="workplaces"
                     control={control}
                     label={t('Workplace')}
                     options={allWorkplaces}
                     disabled={isWorkplaceDisabled}
                     getId={(w) => w.id}
                     getLabel={(w) => w.name ?? ''}
                     required
                  />
               </GridItem> */}

               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={close} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>

                     {!disabled && (
                        <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                           {t(processOwner ? 'Update' : 'Save')}
                        </Button>
                     )}
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
